// Library methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

// Components
import ReportInfo from "./ReportInfo";
import CommentsList from "../../../../components/UI/CommentsList";
import { ErrorMessageComponent } from "../../../../components/UI/ErrorMessage";
import { ReportEachSectionContext } from "../../../../contexts/CommentContext";
import RAPDChart from "../../../../components/UI/RAPDChart";
import RAPDChartLegend from "../../../../components/UI/RAPDChartLegend";

// Utils
import { getDecimalNum } from "../../../../utils/numberHelper";

const EyeReport = () => {
  const { t } = useTranslation();

  const { examReport } = useContext(ReportEachSectionContext);

  // algorithm version number
  const algorithmVersion = examReport?.exam?.version?.substring(0, 3);
  const deviceVersion =
    examReport?.device?.version?.match(/\d+(\.\d+)*/)?.[0] ?? "N/A";

  const rapdResults = useMemo(
    () => examReport?.postProcessing?.rapdResults,
    [examReport]
  );

  const anisocoriaResults = useMemo(
    () => examReport?.postProcessing?.anisocoriaResults,
    [examReport]
  );

  // only show report if there's data
  if (!examReport) return null;

  // build the report content
  return (
    <div className="retinalogik-section retinalogik-report">
      <ReportInfo />
      {examReport?.postProcessing ? (
        <>
          <Grid container gap="20px" marginTop="40px">
            <Grid item xs={12} display="flex" flexDirection="column" gap="20px">
              <Typography fontSize={20}>{t("pupil_diameter_time")}</Typography>
              <RAPDChartLegend />
              <RAPDChart
                chartData={examReport?.postProcessing}
                testDuration={examReport?.timing?.elapsed}
              />
            </Grid>
            <Grid
              item
              xs={12}
              display="flex"
              flexWrap="wrap"
              padding="20px"
              border="1px solid black"
              borderRadius="8px"
              rowGap="8px"
            >
              <Grid item xs={6}>
                <Typography>
                  <span style={{ fontWeight: 600 }}>
                    {t("rapd_detected")}:{" "}
                  </span>
                  {rapdResults?.hasRAPD ? t("word_yes") : t("word_no")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <span style={{ fontWeight: 600 }}>{t("severity")}: </span>
                  {rapdResults?.severity
                    ? t(`word_${rapdResults?.severity}`)
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <span style={{ fontWeight: 600 }}>{t("affected_eye")}: </span>
                  {rapdResults?.affectedEye
                    ? t(`word_${rapdResults?.affectedEye}`)
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <span style={{ fontWeight: 600 }}>{t("filter_value")}: </span>
                  {!isNaN(rapdResults?.ndFilterValue)
                    ? getDecimalNum(rapdResults?.ndFilterValue, 1)
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <span style={{ fontWeight: 600 }}>{t("rapd_value")}: </span>
                  {!isNaN(rapdResults?.rawLogUnits)
                    ? `${getDecimalNum(rapdResults?.rawLogUnits, 1)} ${t("log_units")}`
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6} />

              <Grid item xs={12} my="20px">
                <Divider color="#5B6D7A" />
              </Grid>

              <Grid item xs={12} mb="8px">
                <Typography>{t("pupil_diameters")}</Typography>
              </Grid>

              <Grid item xs={6} display="flex" flexDirection="column" gap="8px">
                <Typography>{t("left_eye")} (OS)</Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="8px"
                  justifyContent="space-around"
                >
                  <ul
                    style={{
                      listStyleType: "disc",
                      paddingInlineStart: "16px",
                      margin: 0,
                    }}
                  >
                    <li>
                      <Typography>
                        <span style={{ fontWeight: 600 }}>
                          {t("dim_light")}:
                        </span>{" "}
                        {getDecimalNum(anisocoriaResults?.dimLightLeft, 1)} mm
                      </Typography>
                    </li>
                    <li>
                      <Typography my="8px">
                        <span style={{ fontWeight: 600 }}>
                          {t("bright_light")}:
                        </span>{" "}
                        {getDecimalNum(anisocoriaResults?.brightLightLeft, 1)}{" "}
                        mm
                      </Typography>
                    </li>
                  </ul>
                </Box>
              </Grid>
              <Grid item xs={6} display="flex" flexDirection="column" gap="8px">
                <Typography>{t("right_eye")} (OD)</Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="8px"
                  justifyContent="space-around"
                >
                  <ul
                    style={{
                      listStyleType: "disc",
                      paddingInlineStart: "16px",
                      margin: 0,
                    }}
                  >
                    <li>
                      <Typography>
                        <span style={{ fontWeight: 600 }}>
                          {t("dim_light")}:
                        </span>{" "}
                        {getDecimalNum(anisocoriaResults?.dimLightRight, 1)} mm
                      </Typography>
                    </li>
                    <li>
                      <Typography my="8px">
                        <span style={{ fontWeight: 600 }}>
                          {t("bright_light")}:
                        </span>{" "}
                        {getDecimalNum(anisocoriaResults?.brightLightRight, 1)}{" "}
                        mm
                      </Typography>
                    </li>
                  </ul>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid container item xs={12} mt={2}>
            <CommentsList isVisualField={false} />
          </Grid>
          <Grid
            container
            item
            display="flex"
            xs={12}
            flexDirection="row"
            justifyContent={"end"}
            mt={2}
          >
            <Grid item xs={12}>
              <Typography textAlign={"right"}>
                {t("Algorithm Version")}: {algorithmVersion}
              </Typography>
              <Typography textAlign={"right"}>
                {t("device_app_version")}: {deviceVersion}
              </Typography>
            </Grid>
          </Grid>
        </>
      ) : (
        <ErrorMessageComponent errorBit={1} />
      )}
    </div>
  );
};

export default EyeReport;
